// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the voice channels the current user could host a meeting in, for the
// "Konvo meeting link" pickers. Reads observable stores, so call it during render.

import Channels from '@app/features/channel/state/Channels';
import GuildGuilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import type {ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import {ChannelTypes, Permissions} from '@konvo/constants/src/ChannelConstants';

export interface VoiceChannelOption extends ComboboxOption<string> {
	/** `${guildId}:${channelId}` */
	value: string;
	guildId: string;
	channelId: string;
	channelName: string;
	context: string;
}

export function listVoiceChannelOptions(): Array<VoiceChannelOption> {
	const options: Array<VoiceChannelOption> = [];
	for (const guild of GuildGuilds.getGuilds()) {
		const channels = Channels.getGuildChannels(guild.id);
		for (const channel of [...channels].sort((a, b) => {
			const parentA = channels.find((item) => item.id === a.parentId);
			const parentB = channels.find((item) => item.id === b.parentId);
			return (parentA?.position ?? -1) - (parentB?.position ?? -1) || (a.position ?? 0) - (b.position ?? 0);
		})) {
			if (
				channel.type !== ChannelTypes.GUILD_VOICE ||
				!Permission.can(Permissions.VIEW_CHANNEL, channel) ||
				!Permission.can(Permissions.CONNECT, channel)
			)
				continue;
			const category = channels.find((item) => item.id === channel.parentId);
			const context = category ? `${guild.name} / ${category.name}` : guild.name;
			options.push({
				value: `${guild.id}:${channel.id}`,
				label: `${channel.name} · ${context}`,
				guildId: guild.id,
				channelId: channel.id,
				channelName: channel.name ?? '',
				context,
			});
		}
	}
	return options;
}

/** The best default voice channel for a meeting started from `guildId`: that guild's first, else the first anywhere. */
export function defaultVoiceChannel(
	options: ReadonlyArray<VoiceChannelOption>,
	guildId: string | null,
): VoiceChannelOption | null {
	return (guildId && options.find((option) => option.guildId === guildId)) || options[0] || null;
}
